import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Grid, TextField, Typography, CircularProgress, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { db, storage } from '../firebase';
import { getDownloadURL, listAll, ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { collection as fbCollection, getDocs, query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';

type MediaItem = { url?: string; name: string; path: string; loading?: boolean; progress?: number; tempId?: string };

export default function MediaLibraryPage() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [keyword, setKeyword] = useState('');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const findUsagesOfUrl = async (url: string) => {
    // 影響の大きいクエリは避け、必要フィールドだけを走査（Firestoreでは部分フィールド取得不可のためクライアント側でフィルタ）
    const usages: { type: 'FAQ' | 'Notice' | 'ImportantNotice'; id: string; label: string }[] = [];
    try {
      // FAQs
      const faqsSnap = await getDocs(fbQuery(fbCollection(db, 'faqs'), fbOrderBy('order', 'asc')));
      faqsSnap.forEach((docSnap) => {
        const data: any = docSnap.data();
        const inBlocks = Array.isArray(data.blocks) && data.blocks.some((b: any) => JSON.stringify(b || {}).includes(url));
        const inText = (data.answer && typeof data.answer === 'string' && data.answer.includes(url)) || (data.question && typeof data.question === 'string' && data.question.includes(url));
        if (inBlocks || inText) {
          usages.push({ type: 'FAQ', id: docSnap.id, label: data.question || docSnap.id });
        }
      });

      // Notices
      const noticesSnap = await getDocs(fbQuery(fbCollection(db, 'notices'), fbOrderBy('publishedAt', 'desc')));
      noticesSnap.forEach((docSnap) => {
        const data: any = docSnap.data();
        const inBlocks = Array.isArray(data.blocks) && data.blocks.some((b: any) => JSON.stringify(b || {}).includes(url));
        const inText = (data.content && typeof data.content === 'string' && data.content.includes(url)) || (data.title && typeof data.title === 'string' && data.title.includes(url));
        if (inBlocks || inText) {
          usages.push({ type: 'Notice', id: docSnap.id, label: data.title || docSnap.id });
        }
      });

      // Important Notices
      const impSnap = await getDocs(fbQuery(fbCollection(db, 'importantNotices'), fbOrderBy('publishedAt', 'desc')));
      impSnap.forEach((docSnap) => {
        const data: any = docSnap.data();
        const inBlocks = Array.isArray(data.blocks) && data.blocks.some((b: any) => JSON.stringify(b || {}).includes(url));
        const inText = data.content && typeof data.content === 'string' && data.content.includes(url);
        if (inBlocks || inText) {
          usages.push({ type: 'ImportantNotice', id: docSnap.id, label: data.content?.slice(0, 30) || docSnap.id });
        }
      });
    } catch (e) {
      console.warn('メディア参照チェック中にエラーが発生:', e);
    }
    return usages;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const baseRef = ref(storage, 'media');
      const res = await listAll(baseRef);
      setTotalCount(res.items.length);
      setItems(res.items.map((it) => ({ name: it.name, path: it.fullPath, loading: true })));
      res.items.forEach(async (itemRef) => {
        try {
          const url = await getDownloadURL(itemRef);
          setItems((prev) => prev.map((it) => it.path === itemRef.fullPath ? { ...it, url, loading: false } : it));
        } catch {
          setItems((prev) => prev.map((it) => it.path === itemRef.fullPath ? { ...it, loading: false } : it));
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    try {
      const path = `media/${Date.now()}_${file.name}`;
      const objectRef = ref(storage, path);
      const tempId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      setItems((prev) => [{ name: file.name, path, loading: true, progress: 0, tempId }, ...prev]);
      const task = uploadBytesResumable(objectRef, file);
      task.on('state_changed', (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setItems((prev) => prev.map((it) => it.tempId === tempId ? { ...it, progress } : it));
      }, (error) => {
        console.error('Upload failed:', error);
        setItems((prev) => prev.filter((it) => it.tempId !== tempId));
        alert('アップロードに失敗しました');
      }, async () => {
        const url = await getDownloadURL(objectRef);
        setItems((prev) => prev.map((it) => it.tempId === tempId ? { name: file.name, path, url, loading: false, progress: 100 } : it));
        setFile(null);
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        setTotalCount((c) => (typeof c === 'number' ? c + 1 : c));
      });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('アップロードに失敗しました');
    }
  };

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return items;
    return items.filter((i) => i.name.toLowerCase().includes(k) || (i.url || '').toLowerCase().includes(k));
  }, [items, keyword]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">メディアライブラリ</Typography>
      </Box>

      <Card sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button variant="outlined" disabled={!file} onClick={handleUpload}>アップロード</Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
            {totalCount === null ? '件数取得中…' : `合計 ${totalCount} 件`}
          </Typography>
          <TextField size="small" placeholder="検索" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        </Box>

        {loading && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress />
          </Box>
        )}

        <Grid container spacing={2}>
          {filtered.map((it) => (
            <Grid item xs={6} sm={4} md={3} key={(it.url || it.tempId || it.path)}>
              <Box sx={{ border: '1px solid #eee', borderRadius: 1, p: 1, cursor: 'pointer' }} onClick={() => setPreview(it)}>
                <Box sx={{ position: 'relative', width: '100%', height: 160, borderRadius: 1, overflow: 'hidden', bgcolor: '#f3f3f3' }}>
                  {it.url ? (
                    <img src={it.url} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}
                </Box>
                {typeof it.progress === 'number' && it.progress >= 0 && it.progress < 100 && (
                  <Box sx={{ mt: 0.5 }}>
                    <LinearProgress variant="determinate" value={it.progress} />
                  </Box>
                )}
                <Box sx={{ fontSize: 12, mt: 0.5, wordBreak: 'break-all' }}>{it.name}</Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>プレビュー</DialogTitle>
        <DialogContent>
          {preview?.url ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', borderRadius: 4 }} />
            </Box>
          ) : (
            <Box sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>{preview?.name}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>閉じる</Button>
          <Button color="error" disabled={deleting} onClick={async () => {
            if (!preview) return;
            // 参照チェック
            const usages = preview.url ? await findUsagesOfUrl(preview.url) : [];
            if (usages.length > 0) {
              const list = usages.map((u) => `- ${u.type}: ${u.label} (${u.id})`).join('\n');
              alert(`この画像は以下のコンテンツで使用されています。\n削除できません。\n\n${list}`);
              return;
            }
            if (!window.confirm('この画像を削除しますか？')) return;
            try {
              setDeleting(true);
              await deleteObject(ref(storage, preview.path));
              setItems((prev) => prev.filter((it) => it.path !== preview.path));
              setPreview(null);
              setTotalCount((c) => (typeof c === 'number' ? Math.max(0, c - 1) : c));
            } catch (e) {
              console.error('Delete failed:', e);
              alert('削除に失敗しました');
            } finally {
              setDeleting(false);
            }
          }}>削除</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


